"use strict";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { HotkeysProvider } from "react-hotkeys-hook";
import { IntlProvider } from "react-intl";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { queryClient } from "@/api/core";
import { useInstance } from "@/api/instance";
import { useRegistrationToken } from "@/api/registrations";
import ErrorScreen from "@/components/error-screen";
import Helmet from "@/components/helmet";
import LoadingScreen from "@/components/loading-screen";
import ScrollToTop from "@/components/scroll-to-top";
import AdjustProvider from "@/contexts/adjust-context";
import AiSearchProvider from "@/contexts/ai-search-context";
import AnalyticsProvider, {
  usePageTracking,
} from "@/contexts/analytics-context";
import AuthProvider from "@/contexts/auth/auth-provider";
import { useAuth } from "@/contexts/auth/useAuth";
import { ComposerProvider } from "@/contexts/composer-context";
import { FullScreenProvider } from "@/contexts/full-screen-context";
import { ModalProvider } from "@/contexts/modal-context";
import { PipProvider } from "@/contexts/pip-context";
import { SidebarMenuProvider } from "@/contexts/sidebar-menu-context";
import { StatProvider } from "@/contexts/stat-context";
import { TimelineProvider } from "@/contexts/timeline-context";
import EmbeddedStatus from "@/features/embedded-status";
import PublicLayout from "@/features/public-layout";
import SideloadPage from "@/features/public-pages/sideload-page";
import {
  useAndroidAppDetection,
  useFeatures,
  useTheme,
  useLocale,
} from "@/hooks";
import MESSAGES from "@/locales/messages";
import { captureException } from "@/monitoring";
import { cn } from "@/utils/cn";
import { config } from "@/utils/config";
import { useCachedLocationHandler } from "@/utils/redirect";

import ErrorBoundary from "../components/error-boundary";

import { renderAppRoutes } from "./app-routes";
import { renderAuthRoutes } from "./auth-routes";

const VerifyEmailRedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/verify/email/${token}`} replace />;
};

const SoapboxMount = () => {
  useCachedLocationHandler();
  useAndroidAppDetection();
  usePageTracking();

  const { isLoggedIn } = useAuth();
  const { search } = useLocation();
  const features = useFeatures();
  const authenticatedProfile = config.authenticatedProfile;

  const redirectRootNoLogin = config.redirectRootNoLogin;

  /** Render the auth layout or UI. */
  const renderSwitch = () => {
    // Helper components for routes that need dynamic values
    const TvSignupRedirect = () => (
      <Navigate
        to={`/${search}${search.includes("?") ? "&" : "?"}tv=true`}
        state={{ fromPath: "tv-signup" }}
        replace
      />
    );
    const TvResetPasswordRedirect = () => (
      <Navigate
        to={`/${search}${search.includes("?") ? "&" : "?"}tv=true`}
        state={{ fromPath: "tv-reset-password" }}
        replace
      />
    );
    const RootRedirect = () => <Navigate to={redirectRootNoLogin} replace />;

    return (
      <>
        <Route
          path="/v1/verify_email/:token"
          element={<VerifyEmailRedirect />}
        />

        {/* Redirect from old login/signup to homepage */}
        <Route path="/signup" element={<Navigate to="/" replace />} />
        <Route path="/tv-signup" element={<TvSignupRedirect />} />
        <Route
          path="/tv-reset-password"
          element={<TvResetPasswordRedirect />}
        />

        {!isLoggedIn &&
          (redirectRootNoLogin ? (
            <Route path="/" element={<RootRedirect />} />
          ) : (
            <Route path="/" element={<PublicLayout />} />
          ))}

        {!isLoggedIn && <Route path="/" element={<PublicLayout />} />}

        <Route path="/mobile/:slug?" element={<PublicLayout />} />
        <Route path="/sideload/:slug?" element={<SideloadPage />} />

        {/* Temporary support old /download link */}
        <Route path="/download" element={<Navigate to="/sideload" replace />} />

        {renderAuthRoutes({ isLoggedIn, search })}

        {renderAppRoutes({ features, search, authenticatedProfile })}
      </>
    );
  };

  return (
    <ErrorBoundary>
      <AiSearchProvider>
        <ComposerProvider>
          <ModalProvider>
            <ScrollToTop />
            <Routes>
              <Route path="/embed/:statusId" element={<EmbeddedStatus />} />

              {renderSwitch()}
            </Routes>

            <div id="toaster">
              <Toaster
                position="top-right"
                containerClassName="top-5"
                containerStyle={{ top: 20 }}
              />
            </div>
          </ModalProvider>
        </ComposerProvider>
      </AiSearchProvider>
    </ErrorBoundary>
  );
};

interface SoapboxLoadProps {
  children: React.ReactNode;
}

/** Initial data loader. */
const SoapboxLoad: React.FC<SoapboxLoadProps> = ({ children }) => {
  const { locale } = useLocale();
  const { isInitialLoading: isLoadingRegistrationToken } =
    useRegistrationToken();

  const [messages, setMessages] = useState<Record<string, string>>({});
  const [localeLoading, setLocaleLoading] = useState(true);

  /** Whether to display a loading indicator. */
  const showLoading = [localeLoading, isLoadingRegistrationToken].some(Boolean);

  // Load the user's locale
  useEffect(() => {
    MESSAGES()
      .then((messages) => {
        setMessages(messages);
        setLocaleLoading(false);
      })
      .catch(() => {});
  }, [locale]);

  // intl is part of loading.
  // It's important nothing in here depends on intl.
  if (showLoading) {
    return <LoadingScreen />;
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  );
};

interface SoapboxHeadProps {
  children: React.ReactNode;
}

/** Injects metadata into site head with Helmet. */
const SoapboxHead: React.FC<SoapboxHeadProps> = ({ children }) => {
  const theme = useTheme();
  const { locale, direction } = useLocale();

  return (
    <>
      <Helmet>
        <html
          lang={locale}
          className={cn("h-full", { dark: theme === "dark" })}
        />
        <body
          className="h-full bg-white text-base dark:bg-gray-900"
          dir={direction}
        />
        <meta name="theme-color" content="#5448ee" />
      </Helmet>

      {children}
    </>
  );
};

const InstanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isInitialLoading: isLoadingInstance, isError, error } = useInstance();

  if (isError) {
    captureException(error, {
      tags: {
        component: "InstanceProvider",
      },
    });

    return <ErrorScreen errorCode={error?.code} />;
  }

  if (isLoadingInstance) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

/** The root React node of the application. */
const Soapbox: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HotkeysProvider>
        <InstanceProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <AnalyticsProvider>
                <AdjustProvider>
                  <StatProvider>
                    <TimelineProvider>
                      <PipProvider>
                        <FullScreenProvider>
                          <SoapboxHead>
                            <SidebarMenuProvider>
                              <SoapboxLoad>
                                <SoapboxMount />
                                <ReactQueryDevtools initialIsOpen={false} />
                              </SoapboxLoad>
                            </SidebarMenuProvider>
                          </SoapboxHead>
                        </FullScreenProvider>
                      </PipProvider>
                    </TimelineProvider>
                  </StatProvider>
                </AdjustProvider>
              </AnalyticsProvider>
            </AuthProvider>
          </BrowserRouter>
        </InstanceProvider>
      </HotkeysProvider>
    </QueryClientProvider>
  );
};

export default Soapbox;
